import { type ReactNode, useEffect, useId, useRef } from 'react';
import { INLINE } from './registry';

/**
 * A value that is also the control that changes it.
 *
 * The problem this solves on the request screen: every fact used to be DISPLAYED in one place
 * and EDITED in another, so correcting an estimate meant finding the panel that owns it. The
 * fix is not more panels — it is letting the reader press the thing they are looking at.
 *
 * PRESENTATION FOLLOWS CONTEXT. In a dense field list the trigger stays text-like until it is
 * reached, so the list does not read as a form waiting to be completed. In the status rail,
 * where these are the primary operational facts, CSS promotes editable values to persistent
 * raised controls. Read-only values remain plain in both places.
 *
 * That is a real accessibility hazard if it is the ONLY affordance — a hover-only pencil is
 * invisible to a screen reader and to anyone who never hovers. Two things always carry it:
 * the element is a `button` with `aria-expanded`, so it is announced as an expandable control
 * however it is reached, and its accessible name says what pressing it does. The pencil is
 * decoration on top of both, `aria-hidden` and never load-bearing.
 *
 * OPEN STATE IS THE CALLER'S. One editor is open at a time across the whole screen, which no
 * individual field can know — and two popovers open over each other in a multi-cell rail is
 * exactly the mess this replaced.
 */

export interface InlineEditProps {
  /** What this value is: "Board lane". Read to a screen reader before the value. */
  label: string;
  /** The value as it reads when closed. */
  children: ReactNode;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** The controls. Rendered only while open, so their ids never collide with a closed twin. */
  editor: ReactNode;
  /**
   * `popover` floats the editor over what is below — for the status rail, whose cells are too
   * narrow to grow. `panel` opens it in the flow, for a list that can simply get taller.
   */
  placement?: 'popover' | 'panel';
  /**
   * A value nobody may change from here: it renders as plain text with no control at all.
   * Never a disabled button — a control that is announced and then refuses is worse than one
   * that was never offered, and this screen has two genuinely read-only facts (an approval
   * decision, and anything a client is only shown).
   */
  readOnly?: boolean;
}

export function InlineEdit({
  label,
  children,
  open,
  onOpen,
  onClose,
  editor,
  placement = 'popover',
  readOnly = false,
}: InlineEditProps) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const surface = useRef<HTMLFieldSetElement>(null);
  const wasOpen = useRef(open);

  useEffect(() => {
    if (open && !wasOpen.current) {
      // The first control in the editor, so a keyboard user lands on the thing they opened
      // rather than at the top of the popover.
      surface.current
        ?.querySelector<HTMLElement>(
          'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
        )
        ?.focus();
    } else if (!open && wasOpen.current) {
      // Back to where the press came from. Without this, closing drops focus onto <body> and
      // the next Tab starts again from the top of the document.
      trigger.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  if (readOnly) {
    return <span className={INLINE.trigger}>{children}</span>;
  }

  if (!open) {
    return (
      <button
        type="button"
        ref={trigger}
        className={INLINE.trigger}
        aria-expanded={false}
        aria-controls={id}
        onClick={onOpen}
      >
        {children}
        {/* Said, not merely drawn. The pencil below is for the eye only. */}
        <span className="vh"> — change {label}</span>
        <span className={INLINE.pen} aria-hidden="true">
          <PencilIcon />
        </span>
      </button>
    );
  }

  return (
    <fieldset
      id={id}
      ref={surface}
      className={placement === 'popover' ? INLINE.popover : INLINE.panel}
      onKeyDown={(event) => {
        // Escape closes without saving, from anywhere inside — including out of a select,
        // where the browser's own Escape has already closed the dropdown by the time this runs.
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
      onBlur={(event) => {
        // A non-modal popover that stays open once focus has moved on is a WCAG 2.4.11
        // hazard: the visible focus ring is on the next control, obscured underneath this
        // one. React's `onBlur` bubbles (it is `focusout` under the hood), so this fires for
        // any control losing focus inside the fieldset — a native `<select>` never triggers
        // it while its own dropdown is open, because opening one does not move DOM focus off
        // the select itself.
        //
        // Only when focus lands on something ELSE. A null `relatedTarget` means it went nowhere
        // focusable — a click on the popover's own padding or text, or the window losing focus
        // — and closing then would throw away an edit the reader is still in the middle of.
        const next = event.relatedTarget;
        if (!(next instanceof Node)) return;
        if (surface.current?.contains(next)) return;
        onClose();
      }}
    >
      {/* A real `fieldset` and `legend` rather than `role="group"` + `aria-label`: the legend
          is the element a screen reader announces on entering the group, and it is read once
          rather than on every control inside it. Hidden because the value it names is the
          trigger the reader just pressed — but nested in the flow, since `.vh` is
          `position: absolute` and a bare one as a flex/grid child drops out and shifts its
          siblings (guardrails §3.4). */}
      <legend>
        <span className="vh">{label}</span>
      </legend>
      {editor}
    </fieldset>
  );
}

/**
 * The row Save and Cancel sit in. Its own component so the two buttons cannot drift apart
 * from one editor to the next — there are eleven of them on the request screen.
 */
export function InlineActions({ children }: { children: ReactNode }) {
  return <div className={INLINE.actions}>{children}</div>;
}

function PencilIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <title>Edit</title>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
